import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import AdvancedOptions from '../../../../../components/slides/highlighted/question/components/advancedOptions/AdvancedOptions';
import ElementShadow from '../../../../../components/slides/highlighted/common/elementShadow';
import {COLOR_FIELDS} from '../../../../../components/slides/highlighted/common/fontAndBackgroundColorPicker';
import {ELEMENT} from '../../../../../incase-domain/elements/base/ElementTypes';
import {type TypedTranslation} from '../../../../../i18n';
import {IPresentationTheme, QuestionElement} from '../../../../../incase-domain/presentationTheme';
import {allQuestionThemeKeys, questionElementTypeToThemeKey} from '../../../../../utils/presentationTheme';
import {buildThemeQuestionControlElement} from '../buildThemePreviewElementByType';
import {patchThemeStyles, toThemeFontSize} from '../themeStyle';

interface PresentationThemeQuestionElementProps {
    presentationTheme: IPresentationTheme;
    setPresentationTheme: React.Dispatch<React.SetStateAction<IPresentationTheme>>;
    questionType: ELEMENT;
    shared?: boolean;
}

function questionHintKey(shared: boolean, questionType: ELEMENT): TypedTranslation {
    if (shared) return 'presentationThemesPage.question.sharedHint';
    if (questionType === ELEMENT.QUESTION) return 'presentationThemesPage.question.baseHint';
    return 'presentationThemesPage.question.subTypeHint';
}

export default function PresentationThemeQuestionElement(props: PresentationThemeQuestionElementProps): JSX.Element {
    const {presentationTheme, setPresentationTheme, questionType, shared = false} = props;
    const {t} = useTranslation();

    const themeKey = questionElementTypeToThemeKey[questionType] ?? 'questionElement';
    const mockElement = useMemo(() => buildThemeQuestionControlElement(questionType, presentationTheme), [presentationTheme, questionType]);

    type FontSizeField = 'questionFontSize' | 'fontSize' | 'commentFontSize';
    const FONT_SIZE_FIELD_TO_KEY: Record<FontSizeField, keyof QuestionElement> = {
        questionFontSize: 'questionFontSize',
        fontSize: 'answerFontSize',
        commentFontSize: 'commentFontSize',
    };
    const DIRECT_FIELDS = new Set<string>([
        'questionBackgroundColor',
        'questionBackgroundBorderRadius',
        'questionTextAlign',
        'fontFamily',
        'correctColor',
        'incorrectColor',
        'debatableColor',
        'correctCommentColor',
        'incorrectCommentColor',
        'debatableCommentColor',
        'selectedColor',
        'borderRadius',
        'boxShadow',
    ]);

    const changeHandler = {
        single: (field: string, value: unknown): void => {
            setPresentationTheme((prevTheme) => {
                const {fontSizes} = prevTheme;

                let patch: Partial<QuestionElement> | null = null;

                if (field in FONT_SIZE_FIELD_TO_KEY) {
                    const key = FONT_SIZE_FIELD_TO_KEY[field as FontSizeField];
                    patch = {[key]: toThemeFontSize(value as number, fontSizes)};
                } else if (DIRECT_FIELDS.has(field)) {
                    patch = {[field]: value};
                }

                if (!patch) return prevTheme;

                return patchThemeStyles(prevTheme, shared ? allQuestionThemeKeys : [themeKey], patch);
            });
        },
        multiple: (fields: string[], values: unknown[]): void => {
            fields.forEach((field, index) => {
                changeHandler.single(field, values[index]);
            });
        },
    };

    return (
        <>
            <p style={{opacity: 0.7, fontSize: '0.85em', margin: '0 0 8px'}}>{t(questionHintKey(shared, questionType))}</p>
            <AdvancedOptions
                highlightedElement={mockElement}
                highlightedElementChangeHandler={changeHandler}
                answerColorFields={[
                    COLOR_FIELDS.CORRECT_COLOR,
                    COLOR_FIELDS.INCORRECT_COLOR,
                    COLOR_FIELDS.DEBATABLE_COLOR,
                    ...(!shared && questionType === ELEMENT.MATCH_PAIR_QUESTION ? [COLOR_FIELDS.SELECTED_COLOR] : []),
                ]}
                commentColorFields={[
                    COLOR_FIELDS.CORRECT_COMMENT_COLOR,
                    COLOR_FIELDS.INCORRECT_COMMENT_COLOR,
                    COLOR_FIELDS.DEBATABLE_COMMENT_COLOR,
                ]}
            />
            <ElementShadow highlightedElement={mockElement} highlightedElementChangeHandler={changeHandler} />
        </>
    );
}
